import { useCallback, useEffect, useState, useRef, startTransition } from 'react';
import { ArticleData, getPaywallType, getAuthorName } from '@benzinga/article-manager';
import { IsUserPaywalledReturn, useIsUserPaywalled } from '@benzinga/user-context';
import { DeviceType } from '@benzinga/device-utils';
import { sophiManager } from '@benzinga/ads-utils';
import { MARKET_MOVING_EXCLUSIVES_CHANNEL_ID, WHITELISTED_AUTHORS } from '../constants/paywall';

interface UseArticlePaywallStatusReturn {
  isNotPaywalled: boolean;
  isPaywallActive: boolean;
  setIsPaywallActive: (value: boolean) => void;
  paywall: IsUserPaywalledReturn;
  paywallStyle: IsUserPaywalledReturn['paywallStyle'];
  headerRef: (node: HTMLDivElement | null) => void;
}

export const useArticlePaywallStatus = (
  articleData: ArticleData,
  disablePaywallByPage?: boolean,
  deviceType?: DeviceType | null,
): UseArticlePaywallStatusReturn => {
  const isChanneled = useCallback(
    (tids: number[]) => {
      return articleData?.channels?.some(channel => tids.includes(channel?.tid));
    },
    [articleData?.channels],
  );

  const authorName = getAuthorName(articleData);
  const isAuthorWhitelisted = !!authorName && WHITELISTED_AUTHORS.includes(authorName);
  const isNotPaywalled = isChanneled([MARKET_MOVING_EXCLUSIVES_CHANNEL_ID]) || isAuthorWhitelisted;

  const shouldDisablePaywall = disablePaywallByPage || isAuthorWhitelisted;

  const paywall = useIsUserPaywalled('com/read', 'unlimited-articles', getPaywallType(articleData), true);

  const [isPaywallActive, setIsPaywallActive] = useState(false);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const scrollHandlerRef = useRef<(() => void) | null>(null);
  const isWallImpressionSentRef = useRef(false);

  useEffect(() => {
    if (!articleData?.nodeId) return;
    isWallImpressionSentRef.current = false;
  }, [articleData?.nodeId]);

  const setHeaderRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      headerRef.current = node;
    }
  }, []);

  useEffect(() => {
    if (shouldDisablePaywall) {
      setIsPaywallActive(false);
      return;
    }

    // for reg wall, sophi decision will update after registration
    if (isPaywallActive && !paywall.active) {
      setIsPaywallActive(false);
      return;
    }

    if (isWallImpressionSentRef.current) return;

    if (!headerRef.current || !paywall.active) return;

    const handleScroll = () => {
      if (!headerRef.current) return;

      const navHeader = document.getElementById('navigation-header');
      const navHeaderHeight = navHeader?.offsetHeight ?? 0;

      const headerRect = headerRef.current.getBoundingClientRect();
      const headerHeight = headerRect.height;
      const scrollPosition = window.scrollY;
      const scrollThreshold = headerHeight * 0.4 + (deviceType === 'mobile' ? 0 : navHeaderHeight);

      if (scrollPosition >= scrollThreshold) {
        if (isWallImpressionSentRef.current) return;
        isWallImpressionSentRef.current = true;
        startTransition(() => {
          setIsPaywallActive(true);
          const section = articleData?.channels?.[0]?.name || '';
          sophiManager.trackWallHit('paywall', section);
        });
        if (scrollHandlerRef.current) {
          window.removeEventListener('scroll', scrollHandlerRef.current);
        }
      }
    };

    scrollHandlerRef.current = handleScroll;
    window.addEventListener('scroll', scrollHandlerRef.current);

    return () => {
      if (scrollHandlerRef.current) {
        window.removeEventListener('scroll', scrollHandlerRef.current);
      }
    };
  }, [paywall.active, deviceType, articleData?.channels, shouldDisablePaywall, paywall.paywallStyle, isPaywallActive]);

  return {
    headerRef: setHeaderRef,
    isNotPaywalled,
    isPaywallActive,
    paywall,
    paywallStyle: paywall.paywallStyle,
    setIsPaywallActive,
  };
};
